import type {CSS} from '@pinecast/styles';
import styled, {focusVisibleRing} from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import type {GroupProps} from './Group';
import Group, {VerticalGroup} from './Group';
import {LabelBoundary, useLabelledControl} from './LabelContext';
import {checkedRadioShadow, radioTextStyles} from './Radio';

const NativeButton = styled(
  'button',
  ({$selected}: {$selected: boolean}, designColors) => {
    const UNSELECTED_SHADOW =
      '0 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0)';

    const selectedShadow = `0 0 0 4px ${designColors.accentBlueLight}, 0 0 0 rgba(0, 0, 0, 0)`;
    return {
      backgroundColor: designColors.contentBackground,
      border: `1px solid ${
        $selected
          ? designColors.borderBlueNormal
          : designColors.contentBackground
      }`,
      borderRadius: 4,
      boxShadow: $selected ? selectedShadow : UNSELECTED_SHADOW,
      color: $selected
        ? designColors.contentText
        : designColors.contentTextAccent,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: DEFAULT_FONT,
      fontSize: 16,
      fontWeight: 400,
      padding: '8px 12px',
      position: 'relative',
      textAlign: 'left',
      transition: 'border 0.2s, box-shadow 0.2s, color 0.2s, opacity 0.2s',
      userSelect: 'none',

      ':hover:not(:focus):not(:active)': {
        textShadow: $selected ? undefined : '0 2px 2px rgba(0, 0, 0, 0.15)',
      },
      // On a selected option, the ring lies over the outer half of the 4px
      // selection ring, so it meets `accentBlueLight` inside and the surface
      // outside.
      ...focusVisibleRing(designColors),
    };
  },
  {className: 'Button-nativeButton'},
);

type OptionEntry = {
  disabled: boolean;
  element: HTMLElement;
  select: () => void;
  selected: boolean;
};

type GroupContextValue = {
  register: (id: string, option: OptionEntry) => () => void;
  tabStop: string | null;
};

const GroupContext = React.createContext<GroupContextValue | null>(null);

const inDocumentOrder = (options: Map<string, OptionEntry>) =>
  [...options].sort(([, a], [, b]) =>
    a.element.compareDocumentPosition(b.element) &
    Node.DOCUMENT_POSITION_FOLLOWING
      ? -1
      : 1,
  );

// The group is one tab stop: the selected option, or else the first one that
// is not disabled.
const findTabStop = (options: Map<string, OptionEntry>): string | null => {
  const ordered = inDocumentOrder(options);
  const tabStop =
    ordered.find(([, option]) => option.selected) ??
    ordered.find(([, option]) => !option.disabled) ??
    ordered[0];
  return tabStop ? tabStop[0] : null;
};

const ARROW_STEPS: Record<string, number> = {
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -1,
};

// Room for the 4px ring of a selected option and for a focus ring, so the ring
// of one option does not touch the next.
const DEFAULT_SPACING = 12;
// Options in a column stretch to the width of the group, up to this.
const MAX_COLUMN_WIDTH = 380;

type LayoutProps = Partial<
  Pick<
    GroupProps,
    | 'allowWrap'
    | 'centerChildren'
    | 'spacing'
    | 'verticalSpacing'
    | 'wrapperStyle'
  >
>;

/**
 * The options of one question, as one radio group (the WAI-ARIA radio group
 * pattern): the group is one tab stop, on the selected option, and the arrow
 * keys move to the next or previous option and select it.
 *
 * It also lays out its options, so that all groups look the same. In a column
 * (`direction="vertical"`, the default), the options stretch to the width of
 * the group, up to 380px. In a row (`direction="horizontal"`), they wrap on a
 * narrow screen. Either way they are 12px apart. The props of `Group` and
 * `VerticalGroup` (`spacing`, `wrapperStyle` and so on) change that where a
 * group needs something else.
 *
 * Put it in a `<Label as="div">`, which names it and gives it the help text
 * and the error. (A `<label>` element would hand a click on its text to the
 * first option.) Where no `<Label>` fits, pass `aria-labelledby` with the id of
 * the visible text that names the group, such as a heading.
 */
export const OptionButtonGroup = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  allowWrap = true,
  centerChildren,
  children,
  direction = 'vertical',
  selectionFollowsFocus = true,
  spacing = DEFAULT_SPACING,
  verticalSpacing,
  wrapperStyle,
}: LayoutProps & {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  children: React.ReactNode;
  direction?: 'horizontal' | 'vertical';
  /**
   * Whether the arrow keys select the option they move to, as they do in a
   * native radio group. Pass `false` only where selecting an option does more
   * than set a value — resets other settings, or shows a different form and
   * moves focus into it — so that moving through the options does not do that
   * (WCAG 3.2.2). Space and Enter still select.
   */
  selectionFollowsFocus?: boolean;
}) => {
  const labelling = useLabelledControl({
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  });
  const [options] = React.useState(() => new Map<string, OptionEntry>());
  const [tabStop, setTabStop] = React.useState<string | null>(null);

  const register = React.useCallback(
    (id: string, option: OptionEntry) => {
      options.set(id, option);
      setTabStop(findTabStop(options));
      return () => {
        options.delete(id);
        setTabStop(findTabStop(options));
      };
    },
    [options],
  );
  const context = React.useMemo(
    () => ({register, tabStop}),
    [register, tabStop],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const step = ARROW_STEPS[e.key];
    if (!step || e.altKey || e.ctrlKey || e.metaKey) {
      return;
    }
    const ordered = inDocumentOrder(options).map(([, option]) => option);
    const current = ordered.findIndex(option => option.element === e.target);
    // Only an option's own keys: a field in the group keeps its arrow keys.
    if (current === -1) {
      return;
    }
    e.preventDefault();
    for (let distance = 1; distance < ordered.length; distance++) {
      const next =
        ordered[(current + step * distance + ordered.length) % ordered.length];
      if (!next.disabled) {
        next.element.focus();
        if (selectionFollowsFocus && !next.selected) {
          next.select();
        }
        return;
      }
    }
  };

  const group = {
    ...labelling,
    centerChildren,
    children: (
      <GroupContext.Provider value={context}>
        {/* The group takes the label's name; nothing inside takes it too. */}
        <LabelBoundary>{children}</LabelBoundary>
      </GroupContext.Provider>
    ),
    onKeyDown: handleKeyDown,
    role: 'radiogroup' as const,
    spacing,
  };
  return direction === 'horizontal' ? (
    <Group
      {...group}
      allowWrap={allowWrap}
      verticalSpacing={verticalSpacing}
      wrapperStyle={wrapperStyle}
    />
  ) : (
    <VerticalGroup
      {...group}
      wrapperStyle={{
        alignItems: centerChildren ? 'center' : 'stretch',
        maxWidth: MAX_COLUMN_WIDTH,
        ...wrapperStyle,
      }}
    />
  );
};

type Props = {
  children: React.ReactNode;
  className?: string;
  // Announced as unavailable, skipped by the arrow keys, and not selectable.
  // It stays focusable, so that it can be found and read.
  disabled?: boolean;
  onClick: () => void;
  selected: boolean;
  style?: CSS;
  // Ignored in an `OptionButtonGroup`, which decides the tab stop.
  tabIndex?: number;
  title?: string;
};

/**
 * One choice, drawn as a card: a `role="radio"` button. Put the choices of one
 * question in an `OptionButtonGroup`. Content inside an option must not be
 * interactive, a label or a heading: the option is a button.
 */
const OptionButton = ({
  children,
  className,
  disabled = false,
  onClick,
  selected,
  style,
  tabIndex,
  title,
}: Props) => {
  const group = React.useContext(GroupContext);
  const id = React.useId();
  const ref = React.useRef<HTMLButtonElement>(null);
  const latestOnClick = React.useRef(onClick);
  React.useLayoutEffect(() => {
    latestOnClick.current = onClick;
  });

  const register = group?.register;
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!register || !element) {
      return undefined;
    }
    return register(id, {
      disabled,
      element,
      select: () => latestOnClick.current(),
      selected,
    });
  }, [disabled, id, register, selected]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!disabled) {
      onClick();
    }
  };

  return (
    <NativeButton
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      role="radio"
      $selected={selected}
      className={className}
      onClick={handleClick}
      ref={ref}
      style={style}
      tabIndex={group ? (group.tabStop === id ? 0 : -1) : tabIndex}
      title={title}
      type="button"
    >
      {children}
    </NativeButton>
  );
};

export default React.memo(OptionButton);

// The layout of the label that `Radio` renders, so an option looks the same
// as when it held a real radio.
const RadioRow = styled('span', {display: 'flex'});
/**
 * The text of an option, with a radio drawn before it. For an option that
 * holds more than a line of text beside its radio — a price with its details
 * under it — draw the radio on the first line with this, and put the rest
 * outside it. `OptionButtonWithRadio` is this, around all of an option.
 */
export const RadioMark = styled(
  'span',
  (
    {
      $alignInput,
      $checked,
      $disabled,
    }: {$alignInput: 'top' | 'center'; $checked: boolean; $disabled: boolean},
    designColors,
  ) => {
    const styles = radioTextStyles({$alignInput, $disabled}, designColors);
    return $checked
      ? {
          ...styles,
          '::before': {
            ...styles['::before'],
            boxShadow: checkedRadioShadow(designColors),
          },
        }
      : styles;
  },
);

/**
 * An option that draws a radio beside its text. The radio is only drawn: an
 * input inside the button would be a control nested in a control, and the
 * button is already the radio.
 */
export const OptionButtonWithRadio = ({
  alignInput = 'center',
  children,
  disabled = false,
  name: _name,
  ...rest
}: Props & {
  alignInput?: 'top' | 'center';
  /** Not used: the option has no input to name. Kept for older callers. */
  name?: string;
}) => (
  <OptionButton {...rest} disabled={disabled}>
    <RadioRow>
      <RadioMark
        $alignInput={alignInput}
        $checked={rest.selected}
        $disabled={disabled}
      >
        {children}
      </RadioMark>
    </RadioRow>
  </OptionButton>
);
